/**
 * SE-1883: the two effective-time copy families on the Rotations page.
 *
 * A rotation carries TWO objects that behave in opposite ways, and
 * conflating their copy is the documented failure mode on this surface
 * (`domain/person-group` inv. 3a; ticket AC "A test fails if the grant
 * copy is used on a membership surface"):
 *
 *   MEMBERSHIP     roster data, resolved live, effective IMMEDIATELY,
 *                  and grants the member NOTHING on its own. Removing
 *                  a member takes effect the moment you save.
 *
 *   DIRECTORSHIP   a grant, keyed by email, effective at NEXT SIGN-IN.
 *                  Revoking a director takes effect the next time
 *                  they sign in — meanwhile their current session
 *                  keeps working until it expires (up to 8 hours).
 *
 * Kept in one file so a single test can walk every string and pin the
 * membership-vs-grant colouring. The test is
 * `RotationsPage.copy.test.tsx`. Do not inline "next sign-in", "token",
 * "immediately" or "grants nothing" anywhere else on this page — those
 * are load-bearing tokens the test looks for.
 *
 * The internal Dashboards template (`disclosure.py` in
 * `simpl_dashboards-se-1765/app/src/system_app/pages/auth_admin/`)
 * mirrors this same split with a `TOKEN_TTL_HOURS = 8` constant; the
 * copy here says "up to 8 hours" for symmetry.
 */

// Load-bearing tokens. The membership-vs-grant test walks the whole
// `rotationCopy` module and asserts these appear ONLY in the section it
// expects. Changing a value here is a copy edit; changing a token
// (renaming "next sign-in" to "on next login", say) needs the test to
// track it.
export const TOKENS = {
  membership: {
    /** appears only on membership surfaces */
    immediate: "immediately",
    /** appears only on membership surfaces */
    grantsNothing: "grants this person nothing on its own",
  },
  directorship: {
    /** appears only on directorship surfaces */
    nextSignIn: "next time they sign in",
    /** appears only on directorship revoke surfaces */
    sessionKeepsWorking: "keeps this access until their current session ends",
    /** appears only on directorship revoke surfaces */
    tokenTtl: "up to 8 hours",
  },
} as const;

// -----------------------------------------------------------------------------
// Membership copy — "immediately", "grants nothing", never "next sign-in".
// -----------------------------------------------------------------------------

export const membershipCopy = {
  /**
   * The banner shown near the member-editing controls. Says what
   * membership IS (roster, not access) and when a change takes effect.
   * Reused verbatim between the member list heading and the add-member
   * affordance.
   */
  disclosureBanner:
    "Rotation membership is roster data, not access — it grants this person nothing on its own. What changes is who this rotation's directors cover, and that takes effect immediately.",

  /** After a successful add. */
  memberAdded:
    "Added to the rotation. The change takes effect immediately. Membership grants this person nothing on its own — it changes who this rotation's directors cover.",

  /** After a successful remove. */
  memberRemoved:
    "Removed from the rotation. The change takes effect immediately.",

  /** Multiple members touched in one save. */
  membersSaved: (added: number, removed: number) =>
    `Saved: ${added} added, ${removed} removed. Membership changes take effect immediately and grant nobody anything on their own.`,

  /**
   * When a sync-written membership is offered for removal. The removal
   * IS allowed (SE-1819 D3), but the caller should know the next sync
   * may write it back.
   */
  syncedMembershipRemovalDisclosure: (sourceLabel: string) =>
    `${sourceLabel} keeps writing this membership; removing it here is allowed, but that source's next sync may put it back.`,

  /**
   * `not_found` stale-view signal on the PATCH /members response. The
   * server tells us which remove-ids named no active membership — we
   * tell the user how many "were already not members" so a silent 0
   * archived isn't mistaken for a broken save.
   */
  someAlreadyGone: (count: number) =>
    count === 1
      ? "1 of the people you removed was already not a member."
      : `${count} of the people you removed were already not members.`,
} as const;

// -----------------------------------------------------------------------------
// Directorship copy — "next sign-in", "session", "up to 8 hours", never
// "immediately" or "grants nothing".
// -----------------------------------------------------------------------------

export const directorshipCopy = {
  /**
   * The banner shown near the director-editing controls. Says a
   * directorship grants dashboard access (an SE-1883 acceptance
   * criterion — the director affordance must state this), and when a
   * change takes effect.
   */
  disclosureBanner:
    "A directorship grants dashboard access — a director sees a narrowed view of the rotation's activity. Changes take effect the next time they sign in; signing out and back in picks it up immediately (otherwise it applies within 8 hours, when their current session expires).",

  /** After a successful add / assign. */
  directorAssigned: (directorName: string, rotationName: string) =>
    `${directorName} is now a director of ${rotationName}. They'll have this access the next time they sign in — signing out and back in picks it up immediately.`,

  /**
   * After a successful revoke. The "current session" clause is the
   * load-bearing distinction: revoking here CANNOT force access off
   * right now.
   *
   * Deliberately avoids the word "immediately" — that is the
   * membership-copy token, and the failure-mode test (rightly) refuses
   * to see it on a directorship surface even inside a "disable the
   * account if access must stop right now" clause.
   */
  directorRevoked: (directorName: string, rotationName: string) =>
    `${directorName} is no longer a director of ${rotationName}. They keep this access until their current session ends — when they sign out, or within up to 8 hours, when the session expires. Removing it here cannot force access off right now; disable the account if the access must stop this instant.`,

  /** Label on a director row whose person isn't among the rotation's members. */
  directorNotMemberLabel: "Not a member",

  /** Tooltip on `directorNotMemberLabel`. */
  directorNotMemberTooltip:
    "Directs this rotation but isn't listed among its members. That's allowed — a director doesn't have to be a member. If they're an Attending in this program, you can add them with \"Add a member\" below.",

  /**
   * On the assign form, the affordance's own subtitle. Anchors the
   * "grants dashboard access" wording per the AC.
   */
  assignSubtitle:
    "Designating a director grants them dashboard access. Changes take effect the next time they sign in.",
} as const;

// -----------------------------------------------------------------------------
// Archive copy — the confirm has more weight here than on the internal
// surface, because there is no restore. See the ticket's "deliberate
// divergence from SE-1687 C2" note.
// -----------------------------------------------------------------------------

export const archiveCopy = {
  /**
   * Header line on the archive confirm dialog. The rotation itself can be
   * unarchived from the Archived list, but the directorships archiving
   * revokes are not restored with it — the confirm is the moment to say so.
   */
  confirmHeader: (rotationName: string) =>
    `Archive "${rotationName}"? You can unarchive it later, but the directorships archiving removes are not restored.`,

  /** When zero directors will lose access. */
  noDirectorsLoseAccess:
    "No directors currently hold this rotation, so archiving it takes no access away.",

  /** When directors WILL lose access. */
  directorsLoseAccess: (count: number) =>
    count === 1
      ? "One director will lose their dashboard access to this rotation. Their access continues until the next time they sign in."
      : `${count} directors will lose their dashboard access to this rotation. Their access continues until the next time they sign in.`,

  /**
   * When some directorships cannot be revoked from here (SIMULATED_SSO,
   * MEDQIP, etc.). The ticket calls this out explicitly: archiving must
   * disclose any directorship it cannot revoke.
   */
  syncedDirectorshipsRemain: (count: number) =>
    count === 1
      ? "1 directorship on this rotation was synced from another system and cannot be revoked here. It stays as a record; archiving the rotation confers no access through it."
      : `${count} directorships on this rotation were synced from another system and cannot be revoked here. They stay as records; archiving the rotation confers no access through them.`,

  /** Success toast copy after archiving. */
  archived: (rotationName: string, revoked: number) =>
    revoked === 0
      ? `"${rotationName}" is archived and moved to the Archived list.`
      : `"${rotationName}" is archived. ${revoked} directorship${revoked === 1 ? "" : "s"} revoked; the affected director${revoked === 1 ? "" : "s"} lose access at their next sign-in.`,

  /** Success toast after unarchiving. Directors were revoked at archive. */
  unarchived: (rotationName: string) =>
    `"${rotationName}" is active again. Directors removed when it was archived are not restored — designate them again if needed.`,
} as const;

// -----------------------------------------------------------------------------
// Refusal copy — surfaced when the service returns a 409 / 422 / stale.
// -----------------------------------------------------------------------------

export const refusalCopy = {
  /** 409 on create — an ACTIVE rotation already holds this label. */
  activeLabelCollision: (label: string) =>
    `A rotation named "${label}" already exists in this program. Rename the new one, or archive the existing one first.`,

  /** 409 on rename. */
  renameCollision: (label: string) =>
    `Another active rotation already uses the name "${label}". Choose a different name.`,

  /** 409 on unarchive — an ACTIVE rotation already holds this label. */
  unarchiveCollision: (label: string) =>
    `An active rotation named "${label}" already exists in this program. Rename or archive that one first, then unarchive this one.`,

  /**
   * The write refused because the state has moved since it was read
   * (SE-1883 "A write composed from state that has since changed is
   * refused and re-read"). Server-side there is no ETag; on the
   * membership surface we surface `not_found` and reload; on the
   * directorship surface we reload and prompt for re-confirmation.
   */
  staleWrite:
    "This rotation was updated by someone else while you were editing. Your change was not applied; the latest is shown now — please review and try again.",
} as const;

// -----------------------------------------------------------------------------
// Who-may-do-what copy — the Attending-only member rule and the Rotation
// Director's limited view. Neutral on effective time: no membership or
// directorship tokens.
// -----------------------------------------------------------------------------

export const rotationAccessCopy = {
  /** Create dialog: what `member_roles` means for this rotation. */
  membersAreAttendings:
    "Members of a rotation are Attendings in this program. Trainees can't be added as rotation members.",

  /** Member picker helper text. */
  memberCandidatesAreAttendings:
    "Only Attendings who are already in this program can be added.",

  /** Member picker dropdown, empty: the program has Attendings and every one is already a member. */
  memberCandidatesAllMembers:
    "Every Attending in this program is already a member of this rotation.",

  /** Member picker dropdown, empty: no Attending in the program can be added (none, all Trainees too, or not synced yet). */
  memberCandidatesNone: "This program has no Attendings who can be added.",

  /** Member picker dropdown, empty: what was typed matches none of the Attendings who can be added. */
  memberCandidatesNoMatch: "No Attending in this program who can be added matches that name.",

  /** Director picker helper text. */
  directorCandidatesAreAttendings:
    "Directors are chosen from this program's Attendings, Program Admins and Rotation Directors. Designating someone also gives them the Rotation Director role in this program.",

  /** Banner for a Rotation Director who doesn't administer the program. */
  directorLimitedView:
    "You're viewing the rotations you direct. You can add or remove members from people already in this program. To add someone new to the program, or to change a rotation's name or directors, contact your program leadership.",
} as const;
